/**
 * Users Page
 * Manage users within the current customer organization
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  Dialpad as PinIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

import { useAppSelector } from '../../store/hooks';
import AppBar from '../elements/AppBar';
import PageBody from '../elements/PageBody';
import PageTitle from '../elements/PageTitle';
import useProjectSelector from '../../hooks/useProjectSelector';
import * as api from '../../services/api';

const UsersPage = () => {
  const { activeCustomer } = useAppSelector((state) => state.customer);
  const { ProjectSelectorComponent } = useProjectSelector();
  
  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'user',
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // AST-103: caller PINs for the phone delivery line. pinStatus is
  // { [userId]: { hasPin, createdAt } } -- never the PIN or its hash.
  // newPin holds a freshly generated PIN for the one dialog that shows it;
  // it is never stored anywhere else.
  const [pinStatus, setPinStatus] = useState({});
  const [pinBusyUserId, setPinBusyUserId] = useState(null);
  const [newPin, setNewPin] = useState(null);
  const [pinCopied, setPinCopied] = useState(false);
  const [removePinUser, setRemovePinUser] = useState(null);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    if (!activeCustomer?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.listCustomerUsers(activeCustomer.id);
      setUsers(response.items || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [activeCustomer?.id]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // AST-103: who has a PIN. A failure here is non-fatal -- the column shows
  // "unknown" rather than claiming nobody has one.
  const [pinStatusError, setPinStatusError] = useState(false);
  const fetchPinStatus = useCallback(async () => {
    if (!activeCustomer?.id) return;
    try {
      const res = await api.listCallerPinStatus(activeCustomer.id);
      setPinStatus(res.items || {});
      setPinStatusError(false);
    } catch (err) {
      console.error('Error fetching PIN status:', err);
      setPinStatusError(true);
    }
  }, [activeCustomer?.id]);

  useEffect(() => {
    fetchPinStatus();
  }, [fetchPinStatus]);

  // Generate or reset a PIN. The response is the only time the PIN exists
  // outside the caller's head, so it goes straight into the dialog.
  const handleGeneratePin = async (user) => {
    setPinBusyUserId(user.id);
    try {
      const res = await api.generateCallerPin(activeCustomer.id, user.id);
      setPinCopied(false);
      setNewPin({ pin: res.pin, user, reset: !!res.reset });
      fetchPinStatus();
    } catch (err) {
      console.error('Error generating PIN:', err);
      setSnackbar({ open: true, message: err?.data?.error || err.message || 'Could not generate a PIN', severity: 'error' });
    } finally {
      setPinBusyUserId(null);
    }
  };

  const handleRemovePin = async () => {
    const user = removePinUser;
    if (!user) return;
    setPinBusyUserId(user.id);
    try {
      await api.removeCallerPin(activeCustomer.id, user.id);
      setSnackbar({ open: true, message: `PIN removed for ${user.name || user.email}`, severity: 'success' });
      setRemovePinUser(null);
      fetchPinStatus();
    } catch (err) {
      console.error('Error removing PIN:', err);
      setSnackbar({ open: true, message: err?.data?.error || err.message || 'Could not remove the PIN', severity: 'error' });
    } finally {
      setPinBusyUserId(null);
    }
  };

  // Handle invite user
  const handleInviteUser = async () => {
    if (!formData.email) {
      setFormError('Email is required');
      return;
    }
    
    setFormSubmitting(true);
    setFormError(null);
    
    try {
      await api.createCustomerUser(activeCustomer.id, {
        email: formData.email,
        name: formData.name || null,
        role: formData.role,
      });
      
      setSnackbar({ 
        open: true, 
        message: `Invitation sent to ${formData.email}`, 
        severity: 'success' 
      });
      setInviteModalOpen(false);
      setFormData({ email: '', name: '', role: 'user' });
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      setFormError(err.message || 'Failed to invite user');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle edit user
  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    setFormSubmitting(true);
    setFormError(null);
    
    try {
      await api.updateCustomerUser(activeCustomer.id, selectedUser.id, {
        name: formData.name,
        role: formData.role,
      });
      
      setSnackbar({ 
        open: true, 
        message: 'User updated successfully', 
        severity: 'success' 
      });
      setEditModalOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Error updating user:', err);
      setFormError(err.message || 'Failed to update user');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle delete (disable) user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setFormSubmitting(true);
    
    try {
      await api.deleteCustomerUser(activeCustomer.id, selectedUser.id);
      
      setSnackbar({ 
        open: true, 
        message: 'User deactivated successfully', 
        severity: 'success' 
      });
      setDeleteConfirmOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      setSnackbar({ 
        open: true, 
        message: err.message || 'Failed to deactivate user', 
        severity: 'error' 
      });
    } finally {
      setFormSubmitting(false);
    }
  };

  // Open edit modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
    });
    setFormError(null);
    setEditModalOpen(true);
  };

  // Open delete confirm
  const openDeleteConfirm = (user) => {
    setSelectedUser(user);
    setDeleteConfirmOpen(true);
  };

  const getRoleChip = (role) => {
    const config = {
      admin: { label: 'Admin', color: 'secondary', icon: <AdminIcon sx={{ fontSize: 14 }} /> },
      customerAdmin: { label: 'Customer Admin', color: 'info', icon: <AdminIcon sx={{ fontSize: 14 }} /> },
      user: { label: 'User', color: 'default', icon: <UserIcon sx={{ fontSize: 14 }} /> },
    };
    const { label, color, icon } = config[role] || config.user;
    return <Chip label={label} size="small" color={color} icon={icon} />;
  };

  const getStatusChip = (status, userStatus) => {
    if (userStatus === 'FORCE_CHANGE_PASSWORD') {
      return <Chip label="Pending" size="small" color="warning" />;
    }
    return (
      <Chip 
        label={status} 
        size="small"
        color={status === 'active' ? 'success' : 'default'}
      />
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />
      
      <PageBody>
        {/* Title Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageTitle>
            Organization Users
          </PageTitle>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setFormData({ email: '', name: '', role: 'user' });
                setFormError(null);
                setInviteModalOpen(true);
              }}
              disabled={!activeCustomer}
              sx={{ height: 44 }}
            >
              Invite User
            </Button>
          </Box>
        </Box>

        {activeCustomer && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Managing users for {activeCustomer.name}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #4A4847' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Phone PIN</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          {(user.name || user.email || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {user.name || user.email?.split('@')[0] || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {getRoleChip(user.role)}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(user.status, user.userStatus)}
                    </TableCell>
                    {/* AST-103: Phone PIN. The PIN itself is only ever
                        shown once, in the dialog after generating it. */}
                    <TableCell>
                      {pinBusyUserId === user.id ? (
                        <CircularProgress size={16} aria-label="Working" />
                      ) : pinStatusError ? (
                        <Typography variant="caption" color="text.secondary">Unknown</Typography>
                      ) : pinStatus[user.id]?.hasPin ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip label="Set" size="small" color="success" variant="outlined" />
                          <IconButton
                            size="small"
                            aria-label={`Reset phone PIN for ${user.name || user.email}`}
                            title="Reset PIN"
                            onClick={() => handleGeneratePin(user)}
                            disabled={user.status === 'inactive'}
                          >
                            <PinIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={`Remove phone PIN for ${user.name || user.email}`}
                            title="Remove PIN"
                            onClick={() => setRemovePinUser(user)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          startIcon={<PinIcon />}
                          onClick={() => handleGeneratePin(user)}
                          disabled={user.status === 'inactive'}
                          sx={{ textTransform: 'none' }}
                        >
                          Generate
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.created ? (
                        <>
                          <Typography variant="body2">
                            {new Date(user.created).toLocaleDateString()}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        title="Edit user"
                        onClick={() => openEditModal(user)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        title="Deactivate user" 
                        color="error"
                        onClick={() => openDeleteConfirm(user)}
                        disabled={user.status === 'inactive'}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {activeCustomer 
                        ? 'No users found. Click "Invite User" to add team members.'
                        : 'Please select a customer to view users.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </PageBody>

      {/* Invite User Modal */}
      <Dialog open={inviteModalOpen} onClose={() => setInviteModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            autoFocus
            label="Email Address"
            type="email"
            fullWidth
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            sx={{ mt: 2, mb: '16px' }}
          />
          <TextField
            label="Name (optional)"
            type="text"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: '16px' }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              label="Role"
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteModalOpen(false)} disabled={formSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleInviteUser} 
            variant="contained"
            disabled={formSubmitting}
            sx={{ height: 44 }}
          >
            {formSubmitting ? <CircularProgress size={20} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            disabled
            value={formData.email}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={formData.role}
              label="Role"
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)} disabled={formSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditUser} 
            variant="contained"
            disabled={formSubmitting}
            sx={{ height: 44 }}
          >
            {formSubmitting ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate {selectedUser?.name || selectedUser?.email}? 
            They will no longer be able to access the system.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={formSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error"
            variant="contained"
            disabled={formSubmitting}
          >
            {formSubmitting ? <CircularProgress size={20} /> : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AST-103: the one and only time a PIN is shown. */}
      <Dialog open={!!newPin} onClose={() => setNewPin(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{newPin?.reset ? 'PIN reset' : 'PIN created'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Give this PIN to {newPin?.user?.name || newPin?.user?.email}. They enter it when
            they call the delivery line. <strong>It can&apos;t be shown again</strong> --
            if it&apos;s lost, generate a new one.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 2 }}>
            <Typography
              variant="h3"
              component="p"
              sx={{ fontFamily: 'monospace', letterSpacing: 6, fontWeight: 700 }}
            >
              {newPin?.pin}
            </Typography>
            <IconButton
              aria-label="Copy PIN"
              title="Copy PIN"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(newPin.pin);
                  setPinCopied(true);
                } catch {
                  setPinCopied(false);
                }
              }}
            >
              <CopyIcon />
            </IconButton>
          </Box>
          {pinCopied && <Alert severity="success" sx={{ mt: 1 }}>Copied to the clipboard.</Alert>}
          {newPin?.reset && (
            <Alert severity="info" sx={{ mt: 1 }}>The previous PIN no longer works.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setNewPin(null)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* AST-103: confirm removing a PIN */}
      <Dialog open={!!removePinUser} onClose={() => setRemovePinUser(null)}>
        <DialogTitle>Remove phone PIN</DialogTitle>
        <DialogContent>
          <Typography>
            Remove the delivery-line PIN for {removePinUser?.name || removePinUser?.email}?
            They won&apos;t be able to log deliveries by phone until a new PIN is generated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovePinUser(null)} disabled={!!pinBusyUserId}>Cancel</Button>
          <Button onClick={handleRemovePin} color="error" variant="contained" disabled={!!pinBusyUserId}>
            {pinBusyUserId ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
